import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react';
import { validarCpfCnpj } from '../../../../../helpers/verificadores';
import { formatarCEP, formatarCPFCNPJ, formatarTelefone, permitirApenasNumerosOuSN } from '../../../../../helpers/masks';
import moment, { Moment } from 'moment';
import BasicDatePicker from '../../../../BasicDatePicker';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { ViaCEPResposta } from '../../../../../types/externas/viacep-resultado';
import { useQuery } from 'react-query';
import axiosClient from '../../../../../lib/axios';
import { desformatarCEP } from '../../../../../helpers/unmask';
import SearchIcon from '@mui/icons-material/Search';
import SelectNacionalidade from '../SelectNacionalidade';
import { validarTelefone } from '../../../../../helpers/validarTelefone';

export default function DialogAddEditDadoProcurador({ open, index, onClose }: DialogAddEditDadoProcuradorProps) {

    const { setValue: setMainFormValue, getValues: getMainFormValues } = useFormContext();

    const isEdit = typeof index === 'number';

    const { control, handleSubmit, reset, watch, getValues, setValue } = useForm<IProcuradorForm>({
        defaultValues: {
            nacionalidade: "Brasileiro(a)",
            nome: '',
            cpf: '',
            sexo: '',
            data_nascimento: null,
        },
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf`)],
        queryFn: async () => {
            if (watch(`uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues();
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`uf`, uf)
            setValue(`bairro`, bairro)
            setValue(`cidade`, localidade)
            setValue(`endereco`, logradouro)
        } catch (error) {
            enqueueSnackbar('Erro ao buscar endereço por CEP: ' + error, { variant: 'error' })
            return
        }
    }

    const opcoesSexo = useMemo(() => [
        { value: "M", label: "Masculino" },
        { value: "F", label: "Feminino" },
    ], []);

    useEffect(() => {
        if (open && isEdit) {
            const procuradorAtual = getMainFormValues(`dados_cadastrais.procuradores.${index}`);
            if (procuradorAtual) {
                reset({
                    ...procuradorAtual,
                    data_nascimento: procuradorAtual.data_nascimento
                        ? moment(procuradorAtual.data_nascimento, 'YYYY-MM-DD')
                        : null
                });
            }
        } else if (open && !isEdit) {
            reset({
                nacionalidade: "Brasileiro(a)",
                nome: '',
                cpf: '',
                sexo: '',
                data_nascimento: null,
            });
        }
    }, [open, isEdit, index, reset, getMainFormValues]);

    const handleSave = handleSubmit((data) => {
        try {
            const formattedData = {
                ...data,
                data_nascimento: data.data_nascimento
                    ? moment(data.data_nascimento).format("YYYY-MM-DD")
                    : null
            };

            if (isEdit) {
                setMainFormValue(`dados_cadastrais.procuradores.${index}`, formattedData);
                enqueueSnackbar('Procurador atualizado com sucesso!', { variant: 'success' });
            } else {
                const procuradoresAtuais = getMainFormValues('dados_cadastrais.procuradores') || [];
                setMainFormValue('dados_cadastrais.procuradores', [...procuradoresAtuais, formattedData]);
                enqueueSnackbar('Procurador adicionado com sucesso!', { variant: 'success' });
            }
            onClose();
        } catch (error) {
            enqueueSnackbar('Erro ao salvar procurador', { variant: 'error' });
            console.error('Erro ao salvar procurador:', error);
        }
    }, (errors) => {
        enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'error' });
    });

    const opcoesGrauParentesco = useMemo(() => [
        { value: "Pai", label: "Pai" },
        { value: "Mãe", label: "Mãe" },
        { value: "Filho", label: "Filho" },
        { value: "Irmão", label: "Irmão" },
        { value: "Outros", label: "Outros" },
    ], []);

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle><b>{isEdit ? 'Editar' : 'Adicionar'} Procurador</b></DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Typography fontWeight={'bold'}>Dados Básicos</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="nome"
                            control={control}
                            rules={{ required: 'O nome do procurador é obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    required
                                    label="Nome"
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 40 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="nome_social"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Nome Social"
                                    fullWidth
                                    slotProps={{ htmlInput: { maxLength: 40 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <Controller
                            name="rg"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label='RG'
                                    type="text"
                                    fullWidth
                                    slotProps={{ htmlInput: { maxLength: 20 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <Controller
                            name="cpf"
                            control={control}
                            rules={{
                                required: 'O CPF do procurador é obrigatório',
                                validate: (value) => {
                                    if (!value || value.trim() === "") return "O CPF do procurador é obrigatório";
                                    return validarCpfCnpj(value) || 'CPF inválido';
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    required
                                    label="CPF"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 17 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <Controller
                            name="sexo"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl fullWidth error={invalid} required>
                                    <InputLabel id="sexo-select">Sexo</InputLabel>
                                    <Select
                                        {...field}
                                        labelId="sexo-select"
                                        label="Sexo"
                                    >
                                        {opcoesSexo.map((opcao, index) => (
                                            <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <Controller
                            name="telefone"
                            control={control}
                            rules={{
                                maxLength: { value: 20, message: 'Telefone deve ter no máximo 20 caracteres' },
                                validate: (value) => {
                                    if (!value || value.trim() === "") return true;
                                    const telefoneValido = validarTelefone(value);
                                    return telefoneValido || "Telefone inválido";
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    label="Telefone"
                                    placeholder="Telefone"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 20 } }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`nome_mae`}
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <TextField
                                        {...field}
                                        required
                                        error={invalid}
                                        label="Nome da Mãe"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        helperText={error ? error.message : ""}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`nome_pai`}
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextField
                                        {...field}
                                        label="Nome do Pai"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="data_nascimento"
                            control={control}
                            rules={{
                                required: 'A data de nascimento do procurador é obrigatória',
                                validate: (value) => {
                                    if (!value) return "A data de nascimento do procurador é obrigatória";
                                    const dataMoment = moment(value);
                                    if (!dataMoment.isValid()) return "Data inválida";
                                    if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                                    return true;
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <BasicDatePicker
                                    required
                                    label="Data de Nascimento"
                                    value={field.value}
                                    blockFutureDates
                                    futureDates={moment().subtract(1, 'days')}
                                    onChange={(data) => field.onChange(data)}
                                    formatString="DD/MM/YYYY"
                                    readonly={false}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="nacionalidade"
                            control={control}
                            rules={{ required: 'A nacionalidade do procurador é obrigatória' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <SelectNacionalidade
                                    required
                                    value={field.value}
                                    onChange={(value) => field.onChange(value)}
                                    error={invalid}
                                    errorMessage={error ? error.message : undefined}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="grau_parentesco"
                            control={control}
                            rules={{ required: 'O grau de parentesco do procurador é obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl fullWidth error={invalid}>
                                    <InputLabel id="grau-parentesco-select">Grau de Parentesco</InputLabel>
                                    <Select
                                        {...field}
                                        labelId="grau-parentesco-select"
                                        label="Grau de Parentesco"
                                    >
                                        {opcoesGrauParentesco.map((opcao, index) => (
                                            <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {error && <FormHelperText>{error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography fontWeight={'bold'}>Endereço</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name={`cep`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="CEP"
                                    variant="outlined"
                                    defaultValue={""}
                                    fullWidth
                                    required
                                    onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                    onBlur={handlePesquisarCep}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message || ""}
                                    slotProps={{
                                        input: {
                                            endAdornment: <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handlePesquisarCep}
                                                    edge="end"
                                                >
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name={`endereco`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Endereço"
                                    variant="outlined"
                                    required
                                    fullWidth
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message || ""}
                                    slotProps={{
                                        inputLabel: { shrink: field.value ? true : undefined }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }}>
                        <Controller
                            name={`numero`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Número"
                                    type="text"
                                    variant="outlined"
                                    defaultValue={""}
                                    fullWidth
                                    onChange={(e) => {
                                        field.onChange(permitirApenasNumerosOuSN(e.target.value))
                                    }}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message || ""}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Controller
                            name={`complemento`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Complemento"
                                    defaultValue={""}
                                    variant="outlined"
                                    fullWidth
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message || ""}
                                />
                            )}
                        />
                    </Grid>


                    <Grid size={{ xs: 12, md: 3 }}>
                        <Controller
                            name={`bairro`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Bairro"
                                    variant="outlined"
                                    required
                                    fullWidth
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message || ""}
                                    slotProps={{
                                        inputLabel: { shrink: field.value ? true : undefined }
                                    }}
                                />
                            )}
                        />
                    </Grid>


                    <Grid size={{ xs: 12, md: 2 }}>
                        <Controller
                            name={`uf`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth {...field} error={fieldState.invalid} required>
                                    <InputLabel id="uf-procurador-select">UF</InputLabel>
                                    <Select
                                        labelId="uf-procurador-select"
                                        value={field.value || ''}
                                        label="UF"
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {ufs.map((uf, index) => (
                                            <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                        ))}
                                    </Select>
                                    {fieldState.error && (
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name={`cidade`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <>
                                    <Autocomplete
                                        value={field.value || null}
                                        fullWidth
                                        disablePortal
                                        options={dataCidades}
                                        defaultValue={""}
                                        onChange={(_, v) => field.onChange(v)}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Cidade"
                                                required
                                                defaultValue={""}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message || ""}
                                            />
                                        }
                                    />
                                </>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogAddEditDadoProcuradorProps {
    open: boolean;
    index?: number;
    onClose: () => void;
}

interface IProcuradorForm {
    nome: string;
    nome_social?: string;
    rg: string;
    cpf: string;
    sexo: string;
    telefone?: string
    data_nascimento: Moment | null;
    nacionalidade: string;
    nome_mae?: string;
    nome_pai?: string;
    grau_parentesco?: string;
    //Endereço
    cep?: string;
    endereco?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
}

interface ICidades {
    dataCidades: Array<string>
}